import React from 'react';
import { useQuery, useQueryClient } from 'react-query';
import API from '../api';
const sessionTokenKey = 'session';
function saveSession(token) {
    return localStorage.setItem(sessionTokenKey, token);
}
function removeSession() {
    return localStorage.removeItem(sessionTokenKey);
}
var AuthStateType;
(function (AuthStateType) {
    AuthStateType[AuthStateType["RESTORE_TOKEN"] = 0] = "RESTORE_TOKEN";
    AuthStateType[AuthStateType["SIGN_IN"] = 1] = "SIGN_IN";
    AuthStateType[AuthStateType["SIGN_OUT"] = 2] = "SIGN_OUT";
})(AuthStateType || (AuthStateType = {}));
const initialAuthState = {
    userToken: undefined,
    isLoading: true,
};
const AuthContext = React.createContext({
    signIn: undefined,
    signInWithGoogle: undefined,
    signOut: undefined,
    authState: initialAuthState,
    account: undefined,
    isAccountLoading: false,
});
function AuthProvider({ children }) {
    const queryClient = useQueryClient();
    const [authState, dispatch] = React.useReducer(reducer, initialAuthState);
    const { data: account, isLoading: isAccountLoading } = useQuery(API.Accounts.getKey(), () => API.Accounts.get(), {
        enabled: !!authState.userToken,
    });
    const signIn = React.useCallback(async (email, password) => {
        const r = await API.Auth.login(email, password);
        if (!r.token) {
            return Promise.reject('session token is null');
        }
        dispatch({
            type: AuthStateType.SIGN_IN,
            token: r.token,
        });
        saveSession(r.token);
    }, []);
    const signInWithGoogle = React.useCallback(async (idToken, csrfToken) => {
        const r = await API.Accounts.googleLogin(csrfToken, idToken);
        if (!r.token) {
            return Promise.reject('session token is null');
        }
        dispatch({
            type: AuthStateType.SIGN_IN,
            token: r.token,
        });
        saveSession(r.token);
    }, []);
    const signOut = React.useCallback(async () => {
        try {
            await API.Auth.logout();
        }
        catch (e) {
            console.error(e);
        }
        dispatch({ type: AuthStateType.SIGN_OUT });
        queryClient.removeQueries();
        removeSession();
    }, [queryClient]);
    React.useEffect(() => {
        (async () => {
            try {
                const r = await API.Auth.refreshToken();
                if (r.token) {
                    dispatch({
                        type: AuthStateType.RESTORE_TOKEN,
                        token: r.token,
                    });
                    saveSession(r.token);
                    return;
                }
            }
            catch (e) {
                /* refresh cookie missing/expired — fall through */
            }
            try {
                const stored = localStorage.getItem(sessionTokenKey);
                if (stored) {
                    dispatch({
                        type: AuthStateType.RESTORE_TOKEN,
                        token: stored,
                    });
                    return;
                }
            }
            catch {
                /* ignore storage failures */
            }
            await signOut();
        })();
    }, [signOut]);
    const auth = { signIn, signInWithGoogle, signOut, authState, account, isAccountLoading };
    return React.createElement(AuthContext.Provider, { value: auth }, children);
}
const reducer = (prevState, action) => {
    switch (action.type) {
        case AuthStateType.RESTORE_TOKEN:
        case AuthStateType.SIGN_IN:
            return {
                ...prevState,
                userToken: action.token,
                isLoading: false,
            };
        case AuthStateType.SIGN_OUT:
            return {
                ...prevState,
                userToken: undefined,
                isLoading: false,
            };
    }
};
export { AuthContext, AuthProvider };
